import { Link } from "react-router-dom";
import { PageShell, Section } from "@/components/PageShell";
import { SeoCta, SeoFaq, SeoRelated } from "@/components/SeoBits";
import { SEO_FAQS } from "@/lib/seoPages";

const a = "font-semibold text-[#7FC8A9] underline decoration-[#7FC8A9]/40 underline-offset-2 hover:text-white";

export default function AdhdOverwhelm() {
  return (
    <PageShell
      testid="page-adhd-overwhelm"
      title="When ADHD overwhelm takes over, start here"
      subtitle="A calm way to shrink the day to one thing and get moving again, without pushing harder or backfilling a single missed day."
    >
      <p>
        ADHD overwhelm is what happens when too many open loops compete for the same attention at once, so
        everything feels urgent and nothing feels possible to start. It is not laziness. The harder you push the
        more stuck you get. The way out is not a longer list or more pressure. It is a smaller day.
      </p>

      <Section heading="What overwhelm feels like with ADHD">
        <p>
          You might notice a busy mind that cannot settle on a starting point. You might reread the same list and
          feel your energy drain. You might freeze, or drift to something easy just to escape the noise. None of
          this means you are failing. It means the day got bigger than what you can hold at once, and it needs to
          shrink.
        </p>
      </Section>

      <Section heading="Shrink the day instead of pushing harder">
        <p>
          When you are overwhelmed, the most useful move is to make the day almost embarrassingly small. One
          thing. Not the right thing, not the biggest thing, just one thing you can start. Momentum comes from
          starting, and starting is easier when the target is tiny.
        </p>
        <p>
          If you are stuck right now and cannot even pick, the free{" "}
          <Link to="/what-should-i-do-next" className={a}>What should I do next tool</Link> will choose one thing
          for you to start. It runs in your browser and saves nothing.
        </p>
      </Section>

      <Section heading="Why pushing harder tends to backfire">
        <p>
          When the day already feels like too much, adding pressure rarely helps. It narrows your attention onto
          the feeling of being behind, which makes starting even harder. Shrinking the day works better because it
          changes the question. Instead of how do I do all of this, you ask what is the one small thing I can start
          now. That question has an answer, and an answer is where momentum begins.
        </p>
        <p>
          It also protects your energy. Fighting the whole list at once burns effort on the parts you were never
          going to reach today. Choosing one thing spends that same energy on something real, and it leaves a
          little in reserve for later.
        </p>
      </Section>

      <Section heading="Small starts add up">
        <p>
          A five minute start is not a small thing on a hard day. It breaks the freeze, and once you are moving it
          is often easier to keep going than it was to begin. Some days you will do one block and stop, and that is
          a real day. Other days that first block turns into three. Either way, you spent the day moving instead of
          spending it on guilt.
        </p>
      </Section>

      <Section heading="Low Battery Mode, step by step">
        <p>
          The Today web app has a mode built for exactly this moment. You open the daily view and answer one
          gentle question about how your brain feels today. If you choose overwhelmed, the plan collapses to what
          matters. You see one must do, one quick win, and a short focus timer. The message on the screen is
          simple. Forget the whole list for a moment. Let's get one thing moving. The rest of the day is folded
          into a calm Can wait note, out of sight until you want it.
        </p>
      </Section>

      <Section heading="A short focus timer to get one thing moving">
        <p>
          Under the one quick win there is a focus timer set for five or ten minutes. You are not committing to
          finish anything. You are committing to begin. When the timer ends there is a quiet finish, no loud
          sound, just a small signal that you did a real block of work. Often that first small block is enough to
          loosen the freeze.
        </p>
      </Section>

      <Section heading="Come back to the full plan when you are ready">
        <p>
          Low Battery Mode is a resting place, not a trap. A single button labeled Return to full plan stays
          visible the whole time. When your capacity comes back, you tap it and the normal day is there again,
          unchanged and waiting. If you want a lighter start on paper first, the{" "}
          <Link to="/free-planner" className={a}>free printable planner</Link> gives you the same one priority
          structure.
        </p>
      </Section>

      <Section heading="Where Low Battery Mode lives">
        <p>
          Low Battery Mode and the focus timer are part of the Today web app at commandcenteradhd.com/today, which
          you sign in to with the email you used at checkout. The Notion Command Center is the wider planning
          system and works on the free Notion plan. For the full daily picture, see the{" "}
          <Link to="/adhd-daily-planner" className={a}>daily planner overview</Link>.
        </p>
      </Section>

      <Section heading="What it costs">
        <p>
          The Command Center is a one time purchase of $17, with no subscription and future updates included.
          There is more about the pricing on the{" "}
          <Link to="/adhd-planner-no-subscription" className={a}>no subscription page</Link>.
        </p>
        <SeoCta slug="adhd-overwhelm" />
      </Section>

      <Section heading="Frequently asked questions">
        <SeoFaq faqs={SEO_FAQS["/adhd-overwhelm"]} />
      </Section>

      <SeoRelated
        links={[
          { to: "/adhd-daily-planner", label: "An ADHD daily planner built for real days" },
          { to: "/what-should-i-do-next", label: "Free tool: what should I do next?" },
          { to: "/free-planner", label: "Get the free printable ADHD planner" },
        ]}
      />
    </PageShell>
  );
}
